import { useSelector } from 'app-redux'
import { withTimeout } from 'utils/with-timeout'
import useParamTogglerApi from 'features/param-toggler/param-toggler-api'
import {
	selectParam,
	setParamLocally,
} from 'features/param-toggler/param-toggler-redux'
import { selectIsRoomTransferCountdownActive } from 'interaction-scheme/redux'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useWorkflowUser } from 'room/common/use-workflow-user'
import { selectTraining } from 'session-info/redux'
import { isRecordingPublishError } from 'vcr/juno/recording-publish-error'
import { useJuno } from 'vcr/juno/juno-provider'
import {
	selectLocalRecordStream,
	selectVCRId,
	setLocalRecordStream,
} from 'video-conference-media/redux'
import {
	DEFAULT_RECORDING_INITIALIZATION_STATUS,
	RecordingClient,
	RecordingServiceError,
	resolveRecordingInitializationStatus,
} from './recorder-client.js'
import type {
	RecordingInitializationLifecycleStatus,
	RecordingInitializationStatus,
} from './recorder-client.js'
import {
	getAutoResumePendingStorageKey,
	readAutoResumePending,
	writeAutoResumePending,
} from './recording-auto-resume-storage.js'
import {
	isRecordingPauseRoom,
	isRecordingRoom,
} from './recording-room-types.js'

type RecorderSessionStatus = 'none' | 'paused' | 'recording'
type StartPhase = 'idle' | 'requesting_share' | 'starting' | 'stopping'

/**
 * Removing the screen track from the call is tidying up after the pause:
 * never wait longer than this for Janus.
 */
const JANUS_TRACK_REMOVAL_TIMEOUT_MS = 3_000
/** A pause that failed is tried again after this long, not in a loop. */
const STOP_RETRY_DELAY_MS = 10_000

type UseRecordingOptions = {
	syncWithParam?: boolean
}

export type RecordingUiStatus =
	| RecordingInitializationLifecycleStatus
	| 'loading'
	| 'requesting_share'
	| 'audio_only'
	| 'stopping'
	// The trainer is in a lobby or group room: the recording is paused there.
	| 'paused_away'

export type RecordingUiSeverity = 'info' | 'warning' | 'error'

export interface RecordingUiState {
	active: boolean
	busy: boolean
	canReshare: boolean
	canResume: boolean
	canStart: boolean
	canStop: boolean
	messageKey: string
	needsReshare: boolean
	paused: boolean
	severity: RecordingUiSeverity
	showPersistentNotification: boolean
	status: RecordingUiStatus
}

export type RecordingState = {
	active: boolean
	canReshare: boolean
	confirmResume: () => void
	dismissResumePrompt: () => void
	needsReshare: boolean
	paused: boolean
	pendingResumePrompt: boolean
	reshareRecording: () => Promise<void>
	resumeRecording: () => Promise<void>
	status: RecordingUiStatus
	uiState: RecordingUiState
}

export const getRecordingStatusMessageKey = (
	status: RecordingUiStatus
): string => {
	switch (status) {
		case 'loading':
			return 'room_menu.recording.loading_info'
		case 'not_initialized':
			return 'room_menu.recording.not_initialized_info'
		case 'ready':
			return 'room_menu.recording.ready_info'
		case 'requesting_share':
			return 'room_menu.recording.requesting_share_info'
		case 'starting':
			return 'room_menu.recording.starting_info'
		case 'recording':
			return 'room_menu.recording.active_info'
		case 'audio_only':
			return 'room_menu.recording.audio_only_info'
		case 'stopping':
			return 'room_menu.recording.stopping_info'
		case 'paused_away':
			return 'room_menu.recording.paused_away_info'
		case 'paused':
			return 'room_menu.recording.paused_info'
		case 'paused_track_lost':
			return 'room_menu.recording.paused_track_lost_info'
		case 'finalizing':
			return 'room_menu.recording.finalizing_info'
		case 'completed':
			return 'room_menu.recording.completed_info'
		case 'error':
			return 'room_menu.recording.error_info'
	}
}

export const getRecordingStatusSeverity = (
	status: RecordingUiStatus
): RecordingUiSeverity => {
	switch (status) {
		case 'audio_only':
		case 'paused_track_lost':
			return 'warning'
		case 'error':
			return 'error'
		default:
			return 'info'
	}
}

const toRecorderSessionStatus = (status: string): RecorderSessionStatus => {
	switch (status) {
		case 'paused':
		case 'paused_track_lost':
			return 'paused'
		case 'recording':
			return 'recording'
		default:
			return 'none'
	}
}

const stopStreamTracks = (stream?: MediaStream | null): void => {
	stream?.getTracks().forEach(track => {
		track.stop()
	})
}

async function captureCurrentTab(): Promise<MediaStream> {
	const stream = await navigator.mediaDevices.getDisplayMedia({
		video: {
			displaySurface: 'browser',
			width: { ideal: 1920, max: 1920 },
			height: { ideal: 1080, max: 1080 },
			frameRate: { ideal: 30, max: 30 },
		},
		audio: false,
		preferCurrentTab: true,
		selfBrowserSurface: 'include',
		systemAudio: 'exclude',
		surfaceSwitching: 'exclude',
	} as DisplayMediaStreamOptions)

	return stream
}

export function useRecordingTrainer({
	syncWithParam = true,
}: UseRecordingOptions = {}): RecordingState {
	const recordingParam = useSelector(state => selectParam(state, 'recording'))
	const training = useSelector(state => selectTraining(state.sessionInfo))
	const [recorderSessionStatus, setRecorderSessionStatus] =
		useState<RecorderSessionStatus>('none')
	const [recorderSessionLoaded, setRecorderSessionLoaded] = useState(false)
	const [loadedRecorderSessionScopeKey, setLoadedRecorderSessionScopeKey] =
		useState('')
	const [recorderInitializationStatus, setRecorderInitializationStatus] =
		useState<RecordingInitializationStatus>(
			DEFAULT_RECORDING_INITIALIZATION_STATUS
		)
	const [recorderInitializationLoaded, setRecorderInitializationLoaded] =
		useState(false)
	const [startPhase, setStartPhase] = useState<StartPhase>('idle')
	const [localStatusError, setLocalStatusError] = useState<string>()
	const [pendingResumePrompt, setPendingResumePrompt] = useState(false)
	const currentStreamRef = useRef<MediaStream | null>(null)
	const operationInFlightRef = useRef(false)
	// Bumped on every room change: an operation from an earlier room can no
	// longer hold or release the lock.
	const operationGenerationRef = useRef(0)
	// When the last pause failed; it is tried again after STOP_RETRY_DELAY_MS.
	const stopFailedAtRef = useRef<number | null>(null)
	const stopRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const queuedSyncRef = useRef(false)
	const suppressAutoResumeRef = useRef(false)
	// Bumped whenever this hook writes the recorder status itself (after a
	// start/stop or a refresh that follows one). A background load that was
	// sent before such a write must not overwrite it with an older answer.
	const statusEpochRef = useRef(0)
	const [syncVersion, setSyncVersion] = useState(0)

	const { juno } = useJuno()
	const paramTogglerApi = useParamTogglerApi()
	const joinedRoomAddress = useJoinedRoomAddress()
	const workflowUser = useWorkflowUser()
	const dispatch = useDispatch()
	const roomId = useSelector(selectVCRId)
	const previousRoomIdRef = useRef<string | undefined>(roomId)
	const restoreRecordingSharePendingRef = useRef(false)
	const isRoomTransferCountdownActive = useSelector(
		selectIsRoomTransferCountdownActive
	)
	const localRecordStream = useSelector(selectLocalRecordStream)
	const latestStartContextRef = useRef<{
		sessionId: string
		roomId?: string
		juno: typeof juno
	}>({
		sessionId: training?.id || '',
		roomId,
		juno,
	})

	latestStartContextRef.current = {
		sessionId: training?.id || '',
		roomId,
		juno,
	}

	const recordingClient = useMemo(() => {
		return new RecordingClient()
	}, [])
	const recorderInitialized = recorderInitializationStatus.initialized
	const localRecordingActive = !!localRecordStream
	const recorderSessionRecording = recorderSessionStatus === 'recording'
	const recorderSessionScopeKey = `${training?.id || ''}:${roomId || ''}`
	const isPauseRoom = isRecordingPauseRoom(joinedRoomAddress?.roomType)
	const isInRecordingRoom = isRecordingRoom(joinedRoomAddress?.roomType)
	const recordingActive = localRecordingActive || recorderSessionRecording
	const needsReshare = recorderSessionRecording && !localRecordingActive
	// Only into the recorded room: elsewhere the screen would be shared into a
	// lobby's or group room's call.
	const canReshare =
		needsReshare &&
		isInRecordingRoom &&
		!isRoomTransferCountdownActive &&
		!!roomId &&
		!!juno
	const autoResumeStorageKey = useMemo(
		() =>
			joinedRoomAddress && workflowUser?.id
				? getAutoResumePendingStorageKey(
						joinedRoomAddress.workflowId,
						joinedRoomAddress.interactionSchemeId,
						workflowUser.id
				  )
				: null,
		[joinedRoomAddress, workflowUser?.id]
	)

	const clearAutoResumePending = useCallback(() => {
		if (autoResumeStorageKey) {
			writeAutoResumePending(autoResumeStorageKey, false)
		}
	}, [autoResumeStorageKey])

	const markRecorderSessionStatus = useCallback(
		(status: RecorderSessionStatus) => {
			statusEpochRef.current += 1
			setRecorderSessionStatus(status)
		},
		[]
	)

	const markRecorderInitializationStatus = useCallback(
		(status: RecordingInitializationStatus) => {
			statusEpochRef.current += 1
			setRecorderInitializationStatus(status)
		},
		[]
	)

	const initRecording = useCallback(
		async (sessionId: string) => {
			try {
				await recordingClient.initRecording(sessionId)
			} catch (error) {
				const recorderError = RecordingServiceError.fromUnknown(error)
				if (!recorderError.isInitAlreadyDone()) {
					throw recorderError
				}
			}
		},
		[recordingClient]
	)

	const refreshRecorderInitialization = useCallback(
		async (sessionId: string): Promise<RecordingInitializationStatus> => {
			const status = resolveRecordingInitializationStatus(
				await recordingClient.getRecordingInitializationStatus(sessionId)
			)
			markRecorderInitializationStatus(status)
			return status
		},
		[markRecorderInitializationStatus, recordingClient]
	)

	// The live recorder state comes from the initialization endpoint, which asks
	// recorder-service. GET /sessions/recording/{id} only describes the stored
	// recording (ACTIVE/DELETED/...) and is 404 until the first chunk is saved.
	// Resolves null when the state is unknown (e.g. 503 RECSERV007: the recorder
	// could not be reached), so the last known state is kept.
	const loadRecorderStatus = useCallback(
		async (
			sessionId: string
		): Promise<RecordingInitializationStatus | null> => {
			try {
				return resolveRecordingInitializationStatus(
					await recordingClient.getRecordingInitializationStatus(sessionId)
				)
			} catch (error) {
				const recorderError = RecordingServiceError.fromUnknown(error)
				if (recorderError.statusCode === 404) {
					return DEFAULT_RECORDING_INITIALIZATION_STATUS
				}
				console.warn('Failed to load recording session status', error)
				return null
			}
		},
		[recordingClient]
	)

	const flushQueuedSync = useCallback(() => {
		if (!queuedSyncRef.current) {
			return
		}

		queuedSyncRef.current = false
		setSyncVersion(version => version + 1)
	}, [])

	const completeOperation = useCallback(
		(generation: number) => {
			// Released already by a room change; a newer operation may hold it.
			if (generation !== operationGenerationRef.current) {
				return
			}
			operationInFlightRef.current = false
			flushQueuedSync()
		},
		[flushQueuedSync]
	)

	const hasCurrentStartContext = useCallback(
		(
			expectedSessionId: string,
			expectedRoomId: string | undefined,
			expectedJuno: typeof juno
		): boolean => {
			const current = latestStartContextRef.current

			return (
				current.sessionId === expectedSessionId &&
				current.roomId === expectedRoomId &&
				current.juno === expectedJuno &&
				!!current.roomId &&
				!!current.juno
			)
		},
		[]
	)

	// A start or stop belongs to the room it began in. Once the trainer is in
	// another room it must not block this one: the old room's call may be
	// closing and never answer (trainings-dev, 2026-10-09). Its late result is
	// ignored (hasCurrentStartContext, the generation), and a screen share it
	// was still setting up is stopped.
	const joinedRoomId = joinedRoomAddress?.roomId
	const lastJoinedRoomIdRef = useRef(joinedRoomId)
	useEffect(() => {
		if (lastJoinedRoomIdRef.current === joinedRoomId) {
			return
		}
		lastJoinedRoomIdRef.current = joinedRoomId
		operationGenerationRef.current += 1

		if (!operationInFlightRef.current) {
			return
		}
		console.log(
			'[Recording] Room changed during a recording operation — releasing it'
		)
		operationInFlightRef.current = false
		stopStreamTracks(currentStreamRef.current)
		currentStreamRef.current = null
		setStartPhase('idle')
		flushQueuedSync()
	}, [flushQueuedSync, joinedRoomId])

	useEffect(
		() => () => {
			if (stopRetryTimerRef.current) {
				clearTimeout(stopRetryTimerRef.current)
			}
		},
		[]
	)

	useEffect(() => {
		const previousRoomId = previousRoomIdRef.current
		const recordingEnabled =
			recordingParam === true || recordingParam === 'true'

		if (
			previousRoomId !== undefined &&
			previousRoomId !== roomId &&
			(localRecordingActive || recorderSessionRecording || recordingEnabled)
		) {
			restoreRecordingSharePendingRef.current = true
		}

		previousRoomIdRef.current = roomId
	}, [localRecordingActive, recorderSessionRecording, recordingParam, roomId])

	useEffect(() => {
		if (localRecordingActive || recorderSessionStatus === 'none') {
			restoreRecordingSharePendingRef.current = false
		}
	}, [localRecordingActive, recorderSessionStatus])

	useEffect(() => {
		if (isPauseRoom) {
			console.log(
				'[Recording] Entered pause room — resetting resume prompt state'
			)
			setPendingResumePrompt(false)
			suppressAutoResumeRef.current = false
		}
	}, [isPauseRoom])

	// Release the guard whenever recording is not (or no longer) enabled —
	// including the case where a start attempt failed before recording ever
	// became active, so a later re-enable (recordingParam back to true) isn't
	// silently skipped by a guard left over from that failure.
	useEffect(() => {
		const isRecordingEnabled =
			recordingParam === true || recordingParam === 'true'

		if (!isRecordingEnabled) {
			suppressAutoResumeRef.current = false
		}
	}, [recordingParam])

	useEffect(() => {
		const sessionId = training?.id || ''
		const sessionScopeKey = `${sessionId}:${roomId || ''}`

		if (!sessionId) {
			setRecorderInitializationStatus(DEFAULT_RECORDING_INITIALIZATION_STATUS)
			setRecorderInitializationLoaded(false)
			setRecorderSessionStatus('none')
			setRecorderSessionLoaded(false)
			setLoadedRecorderSessionScopeKey('')
			setLocalStatusError(undefined)
			setStartPhase('idle')
			return
		}

		let cancelled = false
		const requestEpoch = statusEpochRef.current
		setRecorderSessionLoaded(false)

		void loadRecorderStatus(sessionId).then(initializationStatus => {
			if (cancelled) {
				return
			}

			if (initializationStatus && requestEpoch === statusEpochRef.current) {
				setRecorderInitializationStatus(initializationStatus)
				setRecorderSessionStatus(
					toRecorderSessionStatus(initializationStatus.status)
				)
			}
			setRecorderInitializationLoaded(true)
			setLoadedRecorderSessionScopeKey(sessionScopeKey)
			setRecorderSessionLoaded(true)
		})

		return () => {
			cancelled = true
		}
	}, [loadRecorderStatus, roomId, training?.id])

	const publishRecordingTrack = useCallback(
		async ({ ensureRecorderStarted }: { ensureRecorderStarted: boolean }) => {
			const sessionId = training?.id || ''
			if (!sessionId) {
				throw new Error('Training session is not defined')
			}

			if (!roomId || !juno) {
				console.warn('[Recording] Cannot start yet: the call is not connected')
				return
			}

			if (isRoomTransferCountdownActive) {
				console.warn('[Recording] Cannot start during a room transfer')
				return
			}

			restoreRecordingSharePendingRef.current = false
			setLocalStatusError(undefined)

			if (operationInFlightRef.current) {
				console.warn(
					'[Recording] Cannot start: another recording operation is still running'
				)
				return
			}
			operationInFlightRef.current = true
			const generation = operationGenerationRef.current
			setStartPhase('requesting_share')
			const previousRecorderSessionStatus = recorderSessionStatus
			let stream: MediaStream | null = null
			let junoRecordingStarted = false
			let junoStartAttempted = false
			const expectedRoomId = roomId
			const expectedJuno = juno

			// Bounded: the call may be closing and Janus never answer.
			const rollbackStartedJunoRecording = async (warningMessage: string) => {
				try {
					await withTimeout(
						expectedJuno.stopRecording(),
						JANUS_TRACK_REMOVAL_TIMEOUT_MS,
						'Removing the recording track'
					)
				} catch (stopError) {
					console.warn(warningMessage, stopError)
				}
			}

			try {
				stream = await captureCurrentTab()
				currentStreamRef.current = stream
				setStartPhase('starting')

				if (!hasCurrentStartContext(sessionId, expectedRoomId, expectedJuno)) {
					stopStreamTracks(stream)
					currentStreamRef.current = null
					return
				}

				junoStartAttempted = true
				try {
					await expectedJuno.startRecording(sessionId, stream)
				} catch (error) {
					if (!isRecordingPublishError(error)) {
						throw error
					}

					await rollbackStartedJunoRecording(
						'Failed to reset Juno recording state before retrying recording publish'
					)
					await expectedJuno.startRecording(sessionId, stream)
				}

				junoRecordingStarted = true
				dispatch(setLocalRecordStream(stream))

				if (!hasCurrentStartContext(sessionId, expectedRoomId, expectedJuno)) {
					await rollbackStartedJunoRecording(
						'Failed to roll back stale Juno recording start'
					)

					stopStreamTracks(stream)
					currentStreamRef.current = null
					dispatch(setLocalRecordStream())
					return
				}

				if (ensureRecorderStarted) {
					if (!recorderInitialized) {
						await initRecording(sessionId)
						markRecorderInitializationStatus({
							initialized: true,
							status: 'ready',
						})
					}

					if (
						!hasCurrentStartContext(sessionId, expectedRoomId, expectedJuno)
					) {
						await rollbackStartedJunoRecording(
							'Failed to roll back stale Juno recording start'
						)

						stopStreamTracks(stream)
						currentStreamRef.current = null
						dispatch(setLocalRecordStream())
						return
					}

					await recordingClient.startRecording(sessionId, roomId)
					markRecorderSessionStatus('recording')
				}

				// Whatever was paused by a room transition is running again.
				clearAutoResumePending()
			} catch (error) {
				if (generation !== operationGenerationRef.current) {
					// The trainer has left the room this start was for; that room's
					// state is gone. Only the screen share is cleaned up.
					stopStreamTracks(stream)
					throw error
				}

				// Block syncWithParam from immediately re-triggering startRecording
				// (e.g. re-showing the getDisplayMedia popup) while recordingParam
				// is toggled off below — closes the race before that toggle's SSE
				// echo lands.
				suppressAutoResumeRef.current = true

				if (junoRecordingStarted || junoStartAttempted) {
					await rollbackStartedJunoRecording(
						'Failed to roll back Juno recording start'
					)
				}

				setLocalStatusError(RecordingServiceError.fromUnknown(error).message)

				markRecorderSessionStatus(previousRecorderSessionStatus)
				stopStreamTracks(stream)
				currentStreamRef.current = null
				dispatch(setLocalRecordStream())
				await refreshRecorderInitialization(sessionId).catch(refreshError => {
					console.warn(
						'Failed to refresh recording initialization status after start failure',
						refreshError
					)
				})

				if (joinedRoomAddress) {
					await paramTogglerApi
						.toggleOffParam(joinedRoomAddress, 'recording')
						.catch(toggleError => {
							console.warn(
								'Failed to toggle off recording param after start failure',
								toggleError
							)
						})
				}

				throw error
			} finally {
				if (generation === operationGenerationRef.current) {
					setStartPhase('idle')
				}
				completeOperation(generation)
			}
		},
		[
			clearAutoResumePending,
			completeOperation,
			dispatch,
			hasCurrentStartContext,
			initRecording,
			joinedRoomAddress,
			juno,
			isRoomTransferCountdownActive,
			markRecorderInitializationStatus,
			markRecorderSessionStatus,
			paramTogglerApi,
			recorderInitialized,
			recorderSessionStatus,
			recordingClient,
			refreshRecorderInitialization,
			roomId,
			training?.id,
		]
	)

	const startRecording = useCallback(async () => {
		await publishRecordingTrack({
			ensureRecorderStarted: !recorderSessionRecording,
		})
	}, [publishRecordingTrack, recorderSessionRecording])

	const reshareRecording = useCallback(async () => {
		if (!recorderSessionRecording) {
			return
		}

		await publishRecordingTrack({ ensureRecorderStarted: false })
	}, [publishRecordingTrack, recorderSessionRecording])

	const stopRecording = useCallback(async () => {
		const sessionId = training?.id || ''
		if (!sessionId) {
			throw new Error('Training session is not defined')
		}

		setLocalStatusError(undefined)

		if (operationInFlightRef.current) {
			console.warn(
				'[Recording] Cannot stop: another recording operation is still running'
			)
			return
		}
		operationInFlightRef.current = true
		const generation = operationGenerationRef.current
		setStartPhase('stopping')
		let backendStopError: unknown = null
		const streamToStop = currentStreamRef.current ?? localRecordStream
		// Leaving the room closes its call, which removes the track anyway; on a
		// call that is closing Janus never answers (trainings-dev, 2026-10-09).
		const callToTidy = localRecordingActive && isInRecordingRoom ? juno : null

		try {
			// Detached here at once: the trainer's screen stops being shared
			// while the pause is applied. Removing the track from the call runs
			// alongside, bounded; the pause never waits for it.
			stopStreamTracks(streamToStop)
			currentStreamRef.current = null
			dispatch(setLocalRecordStream())
			const trackRemoval = callToTidy
				? withTimeout(
						callToTidy.stopRecording(),
						JANUS_TRACK_REMOVAL_TIMEOUT_MS,
						'Removing the recording track'
				  ).catch(error => {
						console.warn(
							'Failed to remove the recording track from the call',
							error
						)
				  })
				: Promise.resolve()

			const shouldStopBackendSession =
				recorderSessionRecording ||
				recorderInitializationStatus.status === 'recording' ||
				recorderInitializationStatus.status === 'starting'

			if (shouldStopBackendSession) {
				try {
					await recordingClient.stopRecording(sessionId)
					stopFailedAtRef.current = null
					markRecorderSessionStatus('paused')
				} catch (error) {
					stopFailedAtRef.current = Date.now()
					backendStopError = error
				}
			}

			await trackRemoval
			await refreshRecorderInitialization(sessionId).catch(error => {
				console.warn('Failed to refresh recording initialization status', error)
			})

			if (backendStopError) {
				throw backendStopError
			}
		} finally {
			if (generation === operationGenerationRef.current) {
				setStartPhase('idle')
			}
			completeOperation(generation)
		}
	}, [
		completeOperation,
		dispatch,
		isInRecordingRoom,
		juno,
		localRecordStream,
		localRecordingActive,
		markRecorderSessionStatus,
		recorderInitializationStatus,
		recorderSessionRecording,
		recordingClient,
		refreshRecorderInitialization,
		training?.id,
	])

	// Turns the room's `recording` param on for a resume the trainer asked for.
	// It is applied locally at once: the start can finish before the server's
	// echo, and a param still reading false would make syncWithParam stop the
	// recording it just resumed. If the server refuses, the param goes back to
	// false and the recording stops: the room must not be recorded without its
	// recording indicator. suppressAutoResumeRef keeps syncWithParam from adding
	// a second start or popup when the param flips.
	const enableRecordingParamForResume = useCallback(() => {
		if (!joinedRoomAddress) {
			return
		}

		dispatch(setParamLocally('recording', true))
		void paramTogglerApi
			.toggleOnParam(joinedRoomAddress, 'recording')
			.catch(error => {
				console.warn('Failed to re-enable recording param on resume', error)
				dispatch(setParamLocally('recording', false))
			})
	}, [dispatch, joinedRoomAddress, paramTogglerApi])

	const resumeRecording = useCallback(async () => {
		setPendingResumePrompt(false)
		clearAutoResumePending()
		suppressAutoResumeRef.current = true
		enableRecordingParamForResume()
		await startRecording()
	}, [clearAutoResumePending, enableRecordingParamForResume, startRecording])

	const confirmResume = useCallback(() => {
		setPendingResumePrompt(false)
		clearAutoResumePending()
		suppressAutoResumeRef.current = true
		enableRecordingParamForResume()
		void startRecording().catch(error => {
			console.warn('Failed to resume recording', error)
		})
	}, [clearAutoResumePending, enableRecordingParamForResume, startRecording])

	const dismissResumePrompt = useCallback(() => {
		setPendingResumePrompt(false)
		clearAutoResumePending()
		suppressAutoResumeRef.current = true
	}, [clearAutoResumePending])

	const hasSessionId = !!training?.id
	const isStatusLoading =
		hasSessionId &&
		(!recorderInitializationLoaded ||
			!recorderSessionLoaded ||
			loadedRecorderSessionScopeKey !== recorderSessionScopeKey)
	// In a lobby or group room the recording is paused (or being paused), not
	// "audio only" or "screen lost": the trainer is asked to resume it back in
	// the training room.
	const awayFromRecordedRoom =
		isPauseRoom && (recorderSessionStatus !== 'none' || localRecordingActive)
	const status: RecordingUiStatus = awayFromRecordedRoom
		? 'paused_away'
		: isStatusLoading
		? 'loading'
		: startPhase === 'stopping'
		? 'stopping'
		: startPhase === 'requesting_share'
		? 'requesting_share'
		: startPhase === 'starting'
		? 'starting'
		: localRecordingActive
		? 'recording'
		: recorderSessionRecording
		? 'audio_only'
		: recorderSessionStatus === 'paused'
		? recorderInitializationStatus.status === 'paused_track_lost'
			? 'paused_track_lost'
			: 'paused'
		: recorderInitializationStatus.status === 'error' || localStatusError
		? 'error'
		: recorderInitializationStatus.status
	const busy =
		startPhase !== 'idle' ||
		status === 'loading' ||
		status === 'requesting_share' ||
		status === 'starting' ||
		status === 'stopping' ||
		status === 'finalizing'
	const uiState: RecordingUiState = {
		active: recordingActive,
		busy,
		canReshare: canReshare && status === 'audio_only',
		canResume: (status === 'paused' || status === 'paused_track_lost') && !busy,
		canStart:
			(status === 'not_initialized' ||
				status === 'ready' ||
				status === 'error') &&
			!recordingActive &&
			!busy,
		canStop: recordingActive && !busy,
		messageKey: getRecordingStatusMessageKey(status),
		needsReshare,
		paused: recorderSessionStatus === 'paused',
		severity: getRecordingStatusSeverity(status),
		// The resume prompt says it all while it is shown.
		showPersistentNotification:
			!pendingResumePrompt &&
			(status === 'audio_only' ||
				status === 'error' ||
				status === 'paused_track_lost' ||
				status === 'paused_away'),
		status,
	}

	useEffect(() => {
		const sessionId = training?.id || ''

		if (
			!sessionId ||
			(status !== 'starting' &&
				status !== 'recording' &&
				status !== 'audio_only' &&
				status !== 'stopping' &&
				status !== 'paused' &&
				status !== 'paused_away' &&
				status !== 'paused_track_lost' &&
				status !== 'finalizing')
		) {
			return
		}

		let cancelled = false
		const sessionScopeKey = `${sessionId}:${roomId || ''}`
		const intervalId = window.setInterval(() => {
			const requestEpoch = statusEpochRef.current
			void recordingClient
				.getRecordingInitializationStatus(sessionId)
				.then(resolveRecordingInitializationStatus)
				.then(initializationStatus => {
					if (cancelled || requestEpoch !== statusEpochRef.current) {
						return
					}

					setRecorderInitializationStatus(initializationStatus)
					setRecorderInitializationLoaded(true)
					setRecorderSessionStatus(
						toRecorderSessionStatus(initializationStatus.status)
					)
					setRecorderSessionLoaded(true)
					setLoadedRecorderSessionScopeKey(sessionScopeKey)
				})
				.catch(error => {
					console.warn('Failed to poll recording status', error)
				})
		}, 10000)

		return () => {
			cancelled = true
			window.clearInterval(intervalId)
		}
	}, [recordingClient, roomId, status, training?.id])

	const readyToStart =
		!isRoomTransferCountdownActive &&
		recorderInitializationLoaded &&
		recorderSessionLoaded &&
		loadedRecorderSessionScopeKey === recorderSessionScopeKey &&
		!!training?.id &&
		!!roomId &&
		!!juno

	const stopRecordingRef = useRef(stopRecording)
	useEffect(() => {
		stopRecordingRef.current = stopRecording
	})

	// Auto-stop local recording when the server reports paused/paused_track_lost
	// while the frontend still has an active RECORDING track.
	useEffect(() => {
		if (!recorderInitializationLoaded || !localRecordingActive) {
			return
		}
		const serverPaused =
			recorderInitializationStatus.status === 'paused' ||
			recorderInitializationStatus.status === 'paused_track_lost'
		if (serverPaused) {
			void stopRecordingRef.current()
		}
	}, [
		localRecordingActive,
		recorderInitializationLoaded,
		recorderInitializationStatus.status,
	])

	useEffect(() => {
		if (!syncWithParam) {
			return
		}

		const isRecordingEnabled =
			recordingParam === true || recordingParam === 'true'
		const isRecordingDisabled =
			recordingParam === false || recordingParam === 'false'
		const shouldStop =
			(isRecordingDisabled || isPauseRoom) &&
			(localRecordingActive || recorderSessionRecording)
		// Recording happens in the training/webinar room only. In a group room
		// the trainer has a video call too, so without this a param that is still
		// true would start (or offer to resume) recording the group room.
		const shouldStart =
			isRecordingEnabled &&
			isInRecordingRoom &&
			readyToStart &&
			!localRecordingActive &&
			(!recorderSessionRecording || restoreRecordingSharePendingRef.current)

		console.log('[Recording] syncWithParam fired', {
			syncWithParam,
			recordingParam,
			isPauseRoom,
			isInRecordingRoom,
			isRoomTransferCountdownActive,
			recorderInitializationLoaded,
			recorderSessionLoaded,
			recorderSessionStatus,
			loadedRecorderSessionScopeKey,
			recorderSessionScopeKey,
			scopeMatch: loadedRecorderSessionScopeKey === recorderSessionScopeKey,
			roomId,
			hasJuno: !!juno,
			localRecordingActive,
			recorderSessionRecording,
			shouldStart,
			shouldStop,
			suppressAutoResume: suppressAutoResumeRef.current,
			autoResumeStorageKey,
			autoResumePending: autoResumeStorageKey
				? readAutoResumePending(autoResumeStorageKey)
				: null,
		})

		if ((shouldStart || shouldStop) && operationInFlightRef.current) {
			console.log('[Recording] Operation in flight — queuing sync')
			queuedSyncRef.current = true
			return
		}

		if (shouldStart) {
			const isAutoResume =
				!!autoResumeStorageKey && readAutoResumePending(autoResumeStorageKey)

			const serverSessionActive =
				recorderInitializationStatus.status === 'paused' ||
				recorderInitializationStatus.status === 'paused_track_lost' ||
				recorderInitializationStatus.status === 'recording'

			console.log('[Recording] shouldStart=true', {
				isAutoResume,
				serverSessionActive,
				recorderInitializationStatus: recorderInitializationStatus.status,
				suppressAutoResume: suppressAutoResumeRef.current,
				recorderSessionStatus,
				autoResumeStorageKey,
			})

			if (
				(isAutoResume || serverSessionActive) &&
				!suppressAutoResumeRef.current
			) {
				const reason = isAutoResume
					? 'sessionStorage flag'
					: 'server session active'
				console.log(`[Recording] Showing resume popup — reason: ${reason}`)
				suppressAutoResumeRef.current = true
				setPendingResumePrompt(true)
			} else if (!suppressAutoResumeRef.current) {
				console.log(
					'[Recording] No auto-resume pending — starting recording directly'
				)
				void startRecording().catch(error => {
					console.warn('Failed to start recording', error)
				})
			} else {
				console.log(
					'[Recording] suppressAutoResume=true — skipping (popup already shown or dismissed)'
				)
			}
		} else if (shouldStop) {
			const failedAt = stopFailedAtRef.current
			const retryInMs =
				failedAt === null ? 0 : failedAt + STOP_RETRY_DELAY_MS - Date.now()
			if (retryInMs > 0) {
				// The last pause failed just now: try again later, not in a loop.
				if (!stopRetryTimerRef.current) {
					console.log(
						`[Recording] Pause failed — trying again in ${retryInMs} ms`
					)
					stopRetryTimerRef.current = setTimeout(() => {
						stopRetryTimerRef.current = null
						setSyncVersion(version => version + 1)
					}, retryInMs)
				}
				return
			}
			console.log('[Recording] shouldStop=true — stopping recording')
			void stopRecording().catch(error => {
				console.warn('Failed to stop recording', error)
			})
		}
	}, [
		recordingParam,
		recorderInitializationLoaded,
		recorderInitializationStatus.status,
		recorderSessionLoaded,
		recorderSessionStatus,
		isPauseRoom,
		isInRecordingRoom,
		juno,
		isRoomTransferCountdownActive,
		localRecordingActive,
		loadedRecorderSessionScopeKey,
		readyToStart,
		recorderSessionRecording,
		recorderSessionScopeKey,
		roomId,
		syncVersion,
		syncWithParam,
		autoResumeStorageKey,
		startRecording,
		stopRecording,
	])

	// Back in the training room after a grouping or a break, with the
	// recording paused on the way out (useAutomaticRecordingTransition): offer
	// the resume. It has to be a prompt because getDisplayMedia needs the
	// trainer's click, and the room's `recording` param stays off until they
	// confirm, so participants never see a recording indicator that isn't true.
	// The pending flag is cleared on confirm, dismiss or a successful start.
	useEffect(() => {
		if (
			!syncWithParam ||
			!isInRecordingRoom ||
			!autoResumeStorageKey ||
			!readyToStart ||
			localRecordingActive ||
			startPhase !== 'idle' ||
			operationInFlightRef.current ||
			!readAutoResumePending(autoResumeStorageKey)
		) {
			return
		}

		const serverStatus = recorderInitializationStatus.status
		if (serverStatus === 'finalizing' || serverStatus === 'completed') {
			console.log(
				'[Recording] Pending auto-resume dropped — recording already finalized',
				{ serverStatus }
			)
			clearAutoResumePending()
			return
		}

		console.log(
			'[Recording] Back in the recording room with a pending auto-resume — showing resume prompt',
			{ serverStatus }
		)
		setPendingResumePrompt(true)
	}, [
		autoResumeStorageKey,
		clearAutoResumePending,
		isInRecordingRoom,
		localRecordingActive,
		readyToStart,
		recorderInitializationStatus.status,
		startPhase,
		syncWithParam,
	])

	return {
		active: recordingActive,
		canReshare,
		confirmResume,
		dismissResumePrompt,
		needsReshare,
		paused: recorderSessionStatus === 'paused',
		pendingResumePrompt,
		reshareRecording,
		resumeRecording,
		status,
		uiState,
	}
}
